/**
 * Expanded Question Bank: Domain 4 — Verbal & Deductive Reasoning (الاستدلال اللفظي والدلالي)
 * 15 Balanced Deductive & Semantic Templates with Zero Text-Length Bias
 */


function makePremiseCard(badge_ar, badge_en, text_ar, text_en) {
  return {
    ar: `
      <div class="iq-verbal-quote-board" role="region" aria-label="${badge_ar}">
        <span class="iq-verbal-badge">${badge_ar}</span>
        <p class="iq-verbal-quote" dir="auto">${text_ar}</p>
      </div>
    `,
    en: `
      <div class="iq-verbal-quote-board" role="region" aria-label="${badge_en}">
        <span class="iq-verbal-badge">${badge_en}</span>
        <p class="iq-verbal-quote" dir="auto">${text_en}</p>
      </div>
    `
  };
}

function makeTwoPremisesCard(p1_ar, p1_en, p2_ar, p2_en) {
  return {
    ar: `
      <div class="iq-verbal-quote-board" role="region" aria-label="المقدمات المنطقية">
        <div class="iq-verbal-premise-row">
          <p class="iq-verbal-quote" dir="auto"><span class="iq-verbal-badge" style="margin-inline-end:8px;">المقدمة 1:</span>${p1_ar}</p>
        </div>
        <div class="iq-verbal-premise-row" style="margin-top:8px;">
          <p class="iq-verbal-quote" dir="auto"><span class="iq-verbal-badge" style="margin-inline-end:8px;">المقدمة 2:</span>${p2_ar}</p>
        </div>
      </div>
    `,
    en: `
      <div class="iq-verbal-quote-board" role="region" aria-label="Logical Premises">
        <div class="iq-verbal-premise-row">
          <p class="iq-verbal-quote" dir="auto"><span class="iq-verbal-badge" style="margin-inline-end:8px;">Premise 1:</span>${p1_en}</p>
        </div>
        <div class="iq-verbal-premise-row" style="margin-top:8px;">
          <p class="iq-verbal-quote" dir="auto"><span class="iq-verbal-badge" style="margin-inline-end:8px;">Premise 2:</span>${p2_en}</p>
        </div>
      </div>
    `
  };
}

export const EXPANDED_VERBAL_TEMPLATES = [
  // 1. Transitive Syllogism Deduction
  {
    id: 'verbal_transitive_deduction',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'بعض المبتكرين في المؤسسة يتمتعون بمهارات تحليل البيانات المعقدة بالضرورة',
        en: 'Some innovators in the organization necessarily possess complex analytical skills'
      };
      const distractors = [
        {
          ar: 'جميع المبتكرين في المؤسسة يتقنون مهارات تحليل البيانات المعقدة بشكل قاطع',
          en: 'All innovators in the organization definitively master complex analytical skills'
        },
        {
          ar: 'لا يوجد أي مبتكر داخل المؤسسة يفتقر إلى مهارات تحليل البيانات المعقدة إطلاقاً',
          en: 'No innovator in the organization ever lacks complex data analysis skills entirely'
        },
        {
          ar: 'جميع محللي البيانات المعقدة داخل المؤسسة هم مبتكرون معتمدون في كل الأقسام',
          en: 'All complex data analysts in the organization are certified innovators in all sectors'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeTwoPremisesCard(
        'كل الباحثين الاستراتيجيين يتمتعون بمهارات تحليل بيانات معقدة.',
        'All strategic researchers possess complex data analysis competencies.',
        'بعض المبتكرين في المؤسسة يعملون كباحثين استراتيجيين معتمدين.',
        'Some institutional innovators operate as certified strategic researchers.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بناءً على صحة المقدمتين المنطقيتين فقط وبمعزل عن الواقع، أي استنتاج مما يلي يترتب عليهما بالضرورة؟`,
        questionText_en: `Based strictly on the truth of both logical premises, which conclusion necessarily follows?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `بما أن فئة من المبتكرين ينتمون للباحثين، وكل الباحثين يتقنون التحليل، فإن تلك الفئة من المبتكرين تتقن التحليل حتماً دون تعميم على الكل.`,
        explanation_en: `Because some innovators are researchers, and all researchers have analysis skills, those innovators must possess them.`
      };
    }
  },

  // 2. High-Order Analogical Abstraction
  {
    id: 'verbal_abstract_analogy',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const analogies = [
        {
          pairA_ar: 'المحفز : التفاعل الكيميائي',
          pairA_en: 'Catalyst : Chemical Reaction',
          correct_ar: 'الحافز المادي : الإنتاجية المهنية',
          correct_en: 'Financial Incentive : Professional Output',
          distractors_ar: [
            'الوقود الحراري : المركبة الفضائية',
            'التدريب البدني : اللياقة الرياضية',
            'المهندس المعماري : البناء السكني'
          ],
          distractors_en: [
            'Rocket Fuel : Spacecraft Vehicle',
            'Physical Exercise : Athletic Fitness',
            'Design Architect : Residential Housing'
          ],
          relation_ar: 'عامل خارجي يسرّع ويزيد كفاءة العملية دون أن يُستهلك فيها بالكامل'
        },
        {
          pairA_ar: 'البوصلة : الملاحة البحرية',
          pairA_en: 'Compass : Maritime Navigation',
          correct_ar: 'الميزانية المالية : التخطيط التجاري',
          correct_en: 'Operating Budget : Commercial Strategy',
          distractors_ar: [
            'السيارة السريعة : الطريق السريع',
            'الساعة الرقمية : الوقت المسائي',
            'الرادار الجوي : الطائرة الحربية'
          ],
          distractors_en: [
            'Sports Vehicle : Express Highway',
            'Digital Clock : Evening Hours',
            'Air Radar : Military Jet'
          ],
          relation_ar: 'أداة توجيه استرشادية تمنع الانحراف عن المسار المستهدف المخطط له'
        }
      ];

      const a = prng.pick(analogies);
      const allChoices = prng.shuffle([
        { ar: a.correct_ar, en: a.correct_en, isCorrect: true },
        ...a.distractors_ar.map((d, i) => ({ ar: d, en: a.distractors_en[i], isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'التناسب المفاهيمي',
        'Conceptual Analogy',
        `${a.pairA_ar}  ::  [ ؟ ]`,
        `${a.pairA_en}  ::  [ ? ]`
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `حدد الزوج الذي يحمل أدق علاقة وظيفية ومفاهيمية مماثلة للعلاقة بين (${a.pairA_ar}):`,
        questionText_en: `Select the word pair that shares the closest conceptual and functional analogy with (${a.pairA_en}):`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `العلاقة الجوهرية هي: ${a.relation_ar}.`,
        explanation_en: `The underlying relationship matches: ${a.pairA_en}.`
      };
    }
  },

  // 3. Logical Fallacy Identification
  {
    id: 'verbal_fallacy_detection',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'مغالطة الشخصنة (مهاجمة خلفية المتحدث بدلاً من مناقشة صحة حجته الفنية)',
        en: 'Ad Hominem fallacy (attacking speaker personal background rather than merits)'
      };
      const distractors = [
        {
          ar: 'مغالطة رجل القش (تشويه الحجة الأصلية وتقديمها بصيغة هشة وسهلة الدحض)',
          en: 'Straw Man fallacy (distorting the original argument into a caricature)'
        },
        {
          ar: 'مغالطة التعميم المتسرع (بناء حكم شامل وعام بالاعتماد على عينة غير كافية)',
          en: 'Hasty Generalization fallacy (drawing broad conclusions from scarce data)'
        },
        {
          ar: 'مغالطة الاحتكام للمصادرة (افتراض صحة النتيجة المرجوة مسبقاً داخل الحجة)',
          en: 'Begging the Question fallacy (assuming the conclusion within the premise)'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'العبارة المطروحة',
        'Presented Statement',
        '«لا يمكننا قبول مقترح المهندس لتحسين نظام الأمان الرقمي؛ فهو شخص حديث التخرج ولم يمضِ عامين في شركتنا بعد.»',
        '“We cannot accept the engineer’s digital security proposal; they are a recent graduate with less than two years at our firm.”'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هي المغالطة المنطقية الجوهرية التي يرتكبها المتحدث في العبارة المعروضة؟`,
        questionText_en: `Which formal logical fallacy does the speaker commit in the presented statement?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `المتحدث رفض المقترح بناءً على عمر المهندس وخبرته الزمنية دون تقديم أي نقد لمضمون المقترح التقني ذاته.`,
        explanation_en: `The speaker rejected the argument based on personal tenure rather than evaluating its technical merits.`
      };
    }
  },

  // 4. Modus Tollens (Contrapositive Conditional)
  {
    id: 'verbal_conditional_contrapositive',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'إذا لم تكن المنشأة حاصلة على اعتماد الجودة، فإن نظامها البيئي غير مطابق',
        en: 'If a facility lacks quality accreditation, its environmental system is non-compliant'
      };
      const distractors = [
        {
          ar: 'إذا كانت المنشأة حاصلة على اعتماد الجودة، فإن نظامها البيئي مطابق بالضرورة',
          en: 'If a facility holds quality accreditation, its environmental system is compliant'
        },
        {
          ar: 'إذا كان النظام البيئي غير مطابق للمعايير، فإن المنشأة تحصل على الاعتماد فوراً',
          en: 'If environmental systems are non-compliant, accreditation is granted regardless'
        },
        {
          ar: 'جميع المنشآت الحاصلة على اعتماد الجودة تمتلك أنظمة بيئية غير مطابقة للمعايير',
          en: 'All accredited facilities definitively hold non-compliant environmental standards'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'الشرط المنطقي',
        'Logical Conditional',
        '«كل منشأة تطابق المعيار البيئي تنال اعتماد الجودة حتماً»',
        '“Every facility meeting environmental standards inevitably attains quality accreditation.”'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي من العبارات التالية تمثل المعاكس الإيجابي المكافئ منطقياً (Contrapositive) للشرط الموضح؟`,
        questionText_en: `Which statement represents the logically equivalent contrapositive of the given conditional?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `القاعدة المنطقية: (إذا كان أ فإن ب) تكافئ منطقياً (إذا لم يكن ب فإن ليس أ). نفي النتيجة يستلزم نفي المقدمة.`,
        explanation_en: `Logical contrapositive rule: (P -> Q) is logically equivalent to (not Q -> not P).`
      };
    }
  },

  // 5. Unstated Core Assumption
  {
    id: 'verbal_unstated_assumption',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'تأثير الإضاءة الطبيعية في التركيز لا يعتمد فقط على الحالة النفسية العامة للموظفين',
        en: 'Natural lighting benefits on concentration do not rely solely on general employee mood'
      };
      const distractors = [
        {
          ar: 'جميع الموظفين يفضلون العمل في المكاتب المغلقة بدلاً من المساحات المشتركة المفتوحة',
          en: 'All staff prefer working in closed individual offices rather than open plan layouts'
        },
        {
          ar: 'تكلفة استبدال النوافذ الزجاجية تفوق بكثير تكاليف أجهزة الإنارة الكهربائية الحديثة',
          en: 'Replacing glass windows costs far more than modern digital electric lighting fixtures'
        },
        {
          ar: 'المؤسسات المنافسة حققت أرباحاً سنوية مضاعفة بعد تغيير تصميم مكاتبها المعمارية',
          en: 'Competing firms doubled annual earnings after modifying their structural workspaces'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'الحجة المطروحة',
        'Presented Argument',
        '«زيادة مساحة النوافذ في مقر العمل سترفع إنتاجية الفريق حتماً، لأن الإضاءة الطبيعية تعزز مستوى الانتباه والتركيز الذهني لدى الإنسان.»',
        '“Expanding workspace window surfaces will definitively raise team productivity, because daylight enhances human alertness and focus.”'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الافتراض المنطقي الضمني (غير المصرح به) الذي تقوم عليه صحة هذه الحجة الاستنتاجية؟`,
        questionText_en: `What unstated underlying assumption is necessary for this argument's conclusion to hold?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `الافتراض الجوهري يربط بين التركيز الناتج عن الإضاءة وبين رفع الإنتاجية الواقعية في العمل مباشرة.`,
        explanation_en: `The necessary link bridges physical alertness directly to quantifiable productivity gains.`
      };
    }
  },

  // 6. Semantic Nuance Distinctions
  {
    id: 'verbal_semantic_contrast',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'المرونة تنطوي على تكيف واعٍ مع المتغيرات، بينما التردد ينبع من عجز عن حسم القرار',
        en: 'Flexibility involves mindful adaptation, whereas indecision stems from inability to decide'
      };
      const distractors = [
        {
          ar: 'المرونة ترتبط دائماً بالتساهل المفرط، بينما التردد يعكس دقة الملاحظة والحرص الشديد',
          en: 'Flexibility always relates to excess leniency, while indecision reflects extreme rigor'
        },
        {
          ar: 'المرونة سلوك فردي انطوائي بحت، بينما التردد صفة جماعية تتطلب مشاركة عدة أطراف',
          en: 'Flexibility is strictly individual, while indecision is a collective multi-party trait'
        },
        {
          ar: 'المرونة تؤدي حتماً إلى نتائج سلبية، بينما التردد يضمن سلامة القرار بنسبة مائة بالمائة',
          en: 'Flexibility invariably leads to failure, while indecision guarantees absolute safety'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'المفهومان للمقارنة',
        'Concepts for Comparison',
        '[ المرونة الإدارية ] مقابل [ التردد في اتخاذ القرار ]',
        '[ Managerial Flexibility ] vs [ Decisional Hesitation ]'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الفارق الدلالي الدقيق والمنطقي الذي يميز المفهوم الأول عن المفهوم الثاني في علم الإدارة؟`,
        questionText_en: `What is the precise conceptual difference distinguishing the first term from the second?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `المرونة استجابة إيجابية هادفة للتطورات، بينما التردد شلل إجرائي يعيق المضي قدماً في التنفيذ.`,
        explanation_en: `Flexibility represents purposeful adaptation, whereas hesitation is an impasse in decision execution.`
      };
    }
  },

  // 7. Counter-Evidence Weakener
  {
    id: 'verbal_argument_weakener',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'انخفاض معدلات حوادث العمل في نفس الفترة بسبب تقليص ساعات العمل الإلزامية',
        en: 'Accident drops during the same window resulted from reductions in mandatory work hours'
      };
      const distractors = [
        {
          ar: 'ارتفاع رضا الموظفين عن الوجبات الغذائية المقدمة في مطعم المنشأة الصناعية',
          en: 'Employee satisfaction scores for company cafeteria meals increased significantly'
        },
        {
          ar: 'افتتاح فرع توزيع تجاري جديد للمنشأة في إحدى المناطق السكنية المجاورة',
          en: 'A secondary retail distribution center was opened in an adjacent residential sector'
        },
        {
          ar: 'تحديث الموقع الإلكتروني للمنشأة واستبدال الشعار البصري القديم بآخر رقمي',
          en: 'Corporate portal redesign and brand identity refresh were introduced recently'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'الادعاء المطروح',
        'Presented Claim',
        '«برنامج التدريب السلامي الجديد هو السبب الوحيد في انخفاض حوادث المصنع بنسبة 40% خلال الربع الأخير من العام الحالي.»',
        '“The new safety protocol training is the sole driver behind the 40% workplace incident drop in Q4.”'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي من الحقائق التالية، في حال ثبوت صحتها، يمثل أقوى إضعاف ودحض لمنطق هذا الادعاء؟`,
        questionText_en: `Which of the following facts, if true, would most strongly undermine and weaken this claim?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `تقديم تفسير بديل ومؤثر (تقليص ساعات العمل والتعرض للخطر) يضعف الادعاء بأن برنامج التدريب هو السبب الوحيد.`,
        explanation_en: `Presenting an alternative causal factor (reduced exposure hours) directly weakens sole attribution to training.`
      };
    }
  },

  // 8. Epistemological Fact vs Inference
  {
    id: 'verbal_fact_vs_inference',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'انبعاث الضوء وصدور الصوت من الغرفة حقيقة مادية، ووجود شخص بداخلها استنتاج عقلي',
        en: 'Light and sound are observed physical facts, while occupant presence is an inference'
      };
      const distractors = [
        {
          ar: 'كلا الأمرين حقيقة مادية قاطعة لا تقبل الشك أو التأويل في علم المنطق الوضعي',
          en: 'Both occurrences represent incontrovertible empirical facts in formal epistemics'
        },
        {
          ar: 'كلا الأمرين مجرد استنتاج تخميني غير مثبت يفتقر لأي دليل حسي مباشر في الواقع',
          en: 'Both claims are merely speculative hypotheses lacking sensory grounding in reality'
        },
        {
          ar: 'وجود شخص داخل الغرفة حقيقة يقينية، بينما انبعاث الضوء مجرد وهم بصري استنتاجي',
          en: 'Occupancy is an established fact, whereas light radiation is merely a perceptual bias'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'الملاحظة المطروحة',
        'Observed Situation',
        '«الضوء مضاء داخل المكتب وتسمع منه أصوات؛ إذن هناك شخص بداخله»',
        '“The office lights are illuminated and sounds are heard within; therefore someone is inside.”'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `كيف يُصنف علم المعرفة والمنطق التفرقة بين (ملاحظة الضوء والصوت) وبين (الجزم بوجود شخص)؟`,
        questionText_en: `How does epistemic logic distinguish between observing sensory cues and concluding human presence?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `المشاهدات الحسية المباشرة تعتبر حقائق واقعية، بينما إسنادها لوجود إنسان هو استنتاج منطقي محتمل قد يخطئ.`,
        explanation_en: `Sensory outputs are directly observed facts; deducing human presence is a probabilistic inference.`
      };
    }
  },

  // 9. Categorical Taxonomic Exclusion (Odd-One-Out)
  {
    id: 'verbal_taxonomic_exclusion',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'الرادار (جهاز يعتمد على انعكاس موجات كهرومغناطيسية نشطة بدلاً من استقبال الصوت)',
        en: 'Radar (active electromagnetic reflection device rather than acoustic transducer)'
      };
      const distractors = [
        {
          ar: 'السونار (جهاز يعتمد على انتشار الموجات الصوتية الميكانيكية داخل الوسط المائي)',
          en: 'Sonar (mechanical acoustic transducer utilizing sound propagation in water)'
        },
        {
          ar: 'الميكروفون (مستشعر يحول الاهتزازات الصوتية الميكانيكية في الهواء إلى إشارات)',
          en: 'Microphone (sensor converting acoustic mechanical air vibrations into signals)'
        },
        {
          ar: 'السماعة الطبية (أداة صوتية تنقل الموجات الترددية الناتجة عن نبضات الأعضاء الحيوية)',
          en: 'Stethoscope (acoustic conduit transmitting frequency vibrations from biological pulses)'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'عناصر المجموعة',
        'Set of Items',
        '[ السونار • الرادار • الميكروفون • السماعة الطبية ]',
        '[ Sonar • Radar • Microphone • Stethoscope ]'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي عنصر من عناصر المجموعة المعروضة ينفرد بمبدأ فيزيائي مختلف ويعد شاذاً تصنيفياً عن البقية؟`,
        questionText_en: `Which instrument operates on a fundamentally distinct physical domain from the rest?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `السونار والميكروفون والسماعة الطبية أجهزة صوتية تعتمد على موجات ميكانيكية، بينما الرادار يعتمد على موجات كهرومغناطيسية.`,
        explanation_en: `Sonar, microphone, and stethoscope are acoustic devices; radar uses electromagnetic radio waves.`
      };
    }
  },

  // 10. Necessary vs Sufficient Condition
  {
    id: 'verbal_necessary_vs_sufficient',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'وجود الأكسجين شرط لازم لكنه غير كافٍ بذاته لحدوث الاحتراق في الهواء',
        en: 'Presence of oxygen is necessary but not sufficient on its own for combustion'
      };
      const distractors = [
        {
          ar: 'وجود الأكسجين شرط كافٍ تماماً لحدوث الاشتعال الفوري دون الحاجة لحرارة',
          en: 'Oxygen presence is fully sufficient to trigger instant ignition without heat'
        },
        {
          ar: 'وجود الأكسجين لا يمثل شرطاً لازماً ولا كافياً إطلاقاً لحدوث التفاعل الكيميائي',
          en: 'Oxygen presence represents neither a necessary nor sufficient factor for reaction'
        },
        {
          ar: 'وجود الأكسجين يمنع حدوث الاشتعال الكيميائي في بيئات الضغط الجوي العالي',
          en: 'Oxygen presence prevents chemical ignition under elevated atmospheric pressures'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'المسألة المطروحة',
        'Subject Issue',
        'دور غاز الأكسجين في عملية الاشتعال الكيميائي التقليدي',
        'The functional role of oxygen in standard chemical combustion'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `كيف يصف المنطق العلمي العلاقة السببية بين (توفر غاز الأكسجين) وبين (حدوث الاحتراق)؟`,
        questionText_en: `How does formal logic describe the causal conditionality of oxygen in relation to combustion?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `الاحتراق لا يحدث بدون أكسجين (شرط لازم)، لكن مجرد وجود الأكسجين وحده لا يكفي لاشتعال النار دون وقود ومصدر حرارة (غير كافٍ).`,
        explanation_en: `Combustion cannot occur without oxygen (necessary), but oxygen alone does not ignite without heat/fuel (not sufficient).`
      };
    }
  },

  // 11. Intensity Gradation Spectrum
  {
    id: 'verbal_intensity_gradation',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'الترتيب المنطقي المتدرج: استفسار ← مراجعة ← تدقيق ← تمحيص عميق',
        en: 'Progression: Inquiry -> Review -> Audit -> Deep Rigorous Scrutiny'
      };
      const distractors = [
        {
          ar: 'الترتيب المنطقي المتدرج: تمحيص عميق ← استفسار ← مراجعة ← تدقيق',
          en: 'Progression: Deep Scrutiny -> Inquiry -> Review -> Audit'
        },
        {
          ar: 'الترتيب المنطقي المتدرج: تدقيق ← تمحيص عميق ← استفسار ← مراجعة',
          en: 'Progression: Audit -> Deep Scrutiny -> Inquiry -> Review'
        },
        {
          ar: 'الترتيب المنطقي المتدرج: مراجعة ← استفسار ← تمحيص عميق ← تدقيق',
          en: 'Progression: Review -> Inquiry -> Deep Scrutiny -> Audit'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'قائمة المفردات',
        'Vocabulary List',
        '[ تدقيق • استفسار • تمحيص عميق • مراجعة ]',
        '[ Audit • Inquiry • Deep Scrutiny • Review ]'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي من الخيارات يمثل الترتيب التصاعدي الصحيح للكلمات من الأقل عمقاً وصرامة إلى الأكثر دقة وتفصيلاً؟`,
        questionText_en: `Which sequence correctly orders the terms from lowest intensity/rigor to highest?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `التدرج اللغوي يبدأ بسؤال بسيط (استفسار) ثم فحص عام (مراجعة) ثم فحص منظم (تدقيق) ثم أعلى درجات الاستقصاء (تمحيص).`,
        explanation_en: `The spectrum ascends from inquiry to review, advancing to audit, culminating in deep scrutiny.`
      };
    }
  },

  // 12. Metaphorical Structural Mapping
  {
    id: 'verbal_metaphor_mapping',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'الذاكرة الحيوية تشبه شبكة الطرق التي تندثر مساراتها تدريجياً في حال انعدام المرور',
        en: 'Biological memory resembles a trail network fading away in absence of traffic'
      };
      const distractors = [
        {
          ar: 'الذاكرة الحيوية تشبه خزنة حديدية مغلقة بإحكام تحتفظ بمحتوياتها دون أي تبديل',
          en: 'Biological memory resembles an airtight safe storing artifacts unchanged forever'
        },
        {
          ar: 'الذاكرة الحيوية تشبه كاميرا رقمية تسجل تفاصيل المشهد بدقة ثابتة وغير قابلة للتعديل',
          en: 'Biological memory resembles a digital sensor recording footage with static clarity'
        },
        {
          ar: 'الذاكرة الحيوية تشبه كتاباً ورقياً مطبوعاً تظل صفحاته ناصعة ومحفوظة عبر العصور',
          en: 'Biological memory resembles a printed book whose folios stay pristine over eras'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'المبدأ العلمي',
        'Scientific Principle',
        '«تقوى الروابط العصبية بالاسترجاع وتتلاشى بالإهمال المستمر»',
        '“Synaptic connections reinforce through retrieval and fade through prolonged disuse”'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي تشبيه استعاري يعكس بدقة البنية الهيكلية للمبدأ العصبي الموضح في الذاكرة الحيوية؟`,
        questionText_en: `Which metaphorical model most accurately captures the neurobiological principle of memory plasticity?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `المسارات العصبية، كالمسالك الترابية، تتسع بالاستخدام المتكرر وتنطمس عند هجرها وعدم المشي عليها.`,
        explanation_en: `Neural pathways, like physical trails, widen through frequent usage and fade away with disuse.`
      };
    }
  },

  // 13. Cause vs Correlation Pitfall
  {
    id: 'verbal_correlation_vs_causation',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'ارتفاع درجات الحرارة في فصل الصيف هو العامل المشترك المسبب للظاهرتين معاً',
        en: 'Higher seasonal summer temperatures serve as the confounding driver for both'
      };
      const distractors = [
        {
          ar: 'تناول المثلجات المثلجة يقلل من قدرة الإنسان الجسدية على السباحة الآمنة',
          en: 'Consuming chilled desserts directly impairs physiological motor swimming skills'
        },
        {
          ar: 'السباحة في مياه البحر تثير شهية الإنسان وتدفعه حتماً لشراء المزيد من المثلجات',
          en: 'Swimming in marine waters inevitably induces psychological cravings for ice cream'
        },
        {
          ar: 'الارتباط الإحصائي بين المتغيرين يثبت وجود علاقة سببية حتمية في علم الإحصاء',
          en: 'Empirical correlation conclusively proves an incontrovertible causal link in science'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'الملاحظة الإحصائية',
        'Statistical Observation',
        '«تزداد مبيعات المثلجات وحوادث الغرق البحري معاً في نفس الأشهر السنوية بدرجة ارتباط إحصائي عالية جداً (R = 0.92)»',
        '“Ice cream sales and marine drownings surge simultaneously during the same summer months (R = 0.92)”'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو التفسير العلمي والمنطقي الصحيح لهذا الارتباط الإحصائي المرتفع بين الظاهرتين؟`,
        questionText_en: `What is the valid logical explanation reconciling this high correlation between both phenomena?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `وجود متغير ثالث خفي (حرارة الصيف) يدفع الناس لتناول المثلجات والذهاب للسباحة، دون أن تسبب إحداهما الأخرى.`,
        explanation_en: `A confounding third variable (summer heat) drives both behaviors simultaneously without mutual causation.`
      };
    }
  },

  // 14. Essential Defining Attribute
  {
    id: 'verbal_definitional_essence',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'الالتزام الذاتي بالقواعد الأخلاقية بصرف النظر عن وجود رقابة أو مكافأة خارجية',
        en: 'Adherence to internal ethical principles regardless of external oversight or reward'
      };
      const distractors = [
        {
          ar: 'تحقيق أعلى ربحية مالية ممكنة للمؤسسة مع تجنب العقوبات القانونية المعلنة',
          en: 'Maximizing institutional profitability while avoiding public statutory sanctions'
        },
        {
          ar: 'الحصول على استحسان ورضا الزملاء والمديرين في بيئة العمل بشتى الوسائل المتاحة',
          en: 'Gaining peer and managerial social approval within the office by any means'
        },
        {
          ar: 'تجنب الوقوع في أي مجازفة إدارية والامتناع الدائم عن اتخاذ القرارات المصيرية',
          en: 'Steering clear of managerial risk by perpetually deferring decisive mandates'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makePremiseCard(
        'المفهوم الفلسفي',
        'Philosophical Concept',
        '[ النزاهة المهنية والذاتية (Integrity) ]',
        '[ Professional and Personal Integrity ]'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو المعيار الجوهري والشرط اللازم الذي يحدد المفهوم الفلسفي للنزاهة بدقة؟`,
        questionText_en: `Which essential criterion defines the philosophical concept of integrity?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `النزاهة تعرف بفعل الصواب بدافع قيمي داخلي حتى في غياب أي عين تراك أو سلطة تحاسبك.`,
        explanation_en: `Integrity is fundamentally doing what is ethical based on internal conscience, even when unobserved.`
      };
    }
  },

  // 15. Syllogistic Quantifier Distribution
  {
    id: 'verbal_quantifier_distribution',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'لا يمكن الجزم بصحة أو خطأ العلاقة بين الفئتين استناداً لهذه المقدمات وحدها',
        en: 'Validity cannot be determined between both classes based on these premises alone'
      };
      const distractors = [
        {
          ar: 'جميع عناصر الفئة الأولى تنتمي حتماً إلى الفئة الثانية بصورة قطعية لا ريب فيها',
          en: 'All members of the first class definitively belong to the second without doubt'
        },
        {
          ar: 'لا يوجد أي عنصر مشترك على الإطلاق بين عناصر الفئتين في جميع الحالات الممكنة',
          en: 'No common elements can ever exist between both classes across all possibilities'
        },
        {
          ar: 'تتطابق الفئتان بالكامل في كافة الخصائص الجوهرية والسمات التصنيفية التابعة لهما',
          en: 'Both classes match identically across all essential attributes and taxonomy tags'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeTwoPremisesCard(
        'بعض الحواسيب المكتبية مزودة بوحدات معالجة عصبية.',
        'Some desktop computers are equipped with neural processing units.',
        'بعض وحدات المعالجة العصبية تستهلك طاقة كهربائية عالية جداً.',
        'Some neural processing units consume very high electrical power.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الحكم المنطقي السليم حول علاقة (الحواسيب المكتبية) بـ (استهلاك الطاقة الكهربائية العالية)؟`,
        questionText_en: `What is the valid deductive inference regarding desktop computers and high power consumption?`,
        stimulusHtml: stim.ar,
        stimulusHtml_ar: stim.ar,
        stimulusHtml_en: stim.en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `مقدمتان جزئيتان (بعض... بعض...) لا تضمنان وجود تقاطع مؤكد بين الطرفين الخارجيين، فالنتيجة غير محددة منطقياً.`,
        explanation_en: `Two particular premises (some + some) suffer from the undistributed middle fallibility; no conclusion is certain.`
      };
    }
  }
];
